import type {
  FieldDef,
  FormSchema,
  QuestionDef,
} from "../risikoanalyseStep/types";

/**
 * Schemas for the separate "Liquidität" and "Ertragsausfall" risk step forms.
 *
 * Mirrors `for-ai/liquidität-ertrag.json`. Each yes_no question reveals a
 * "Kurzbeschreibung" textarea on `ja`, matching the source JSON's followUp.
 */

const yn = (
  question: string,
  key: string,
  followUpKey?: string,
  hint?: string,
): QuestionDef => ({
  question,
  hint,
  field: { key, type: "yesno" },
  ...(followUpKey
    ? {
        showFollowUpWhen: { key, equals: "ja" },
        followUpFields: [
          {
            key: followUpKey,
            label: "Kurzbeschreibung",
            type: "textarea",
          },
        ] as FieldDef[],
      }
    : {}),
});

const q = (question: string, field: FieldDef, hint?: string): QuestionDef => ({
  question,
  hint,
  field,
});

export const ertragsausfallSchema: FormSchema = {
  sections: [
    {
      key: "ertragsausfall",
      title: "Ertragsausfall-Risiken",
      color: "pillarOrange",
      subSteps: [
        {
          key: "ertragsausfall",
          title: "Ertragsausfall-Risiken",
          description: "zu ergänzen",
          questions: [
            yn(
              "Sind finanzielle Schäden, die durch Betriebsunterbrechung ausgelöst werden, denkbar?",
              "ea_01",
              "ea_01_beschreibung",
            ),
            yn(
              "Sind Mietausfälle aus vermieteten Gebäuden / Räumlichkeiten abzusichern? Vermieten Sie zusätzlich unter?",
              "ea_02",
              "ea_02_beschreibung",
            ),
            yn(
              "Ist eine Betriebsschließung durch die Behörden auf Grund von Seuchengefahr denkbar?",
              "ea_03",
              "ea_03_beschreibung",
            ),
            yn(
              "Gibt es Abhängigkeiten zu Abnehmern und / oder Zulieferern?",
              "ea_04",
              "ea_04_beschreibung",
            ),
            yn(
              "Gibt es Spezialmaschinen und / oder IT-Systeme mit längeren Lieferzeiten?",
              "ea_05",
              "ea_05_beschreibung",
            ),
            yn(
              "Gibt es einen Notfallplan im Schadenfall?",
              "ea_06",
              "ea_06_beschreibung",
            ),
            yn(
              "Könnten Sie bei einem befreundeten Unternehmen produzieren?",
              "ea_07",
              "ea_07_beschreibung",
            ),
            q(
              "Wie lange benötigen Sie, um Ihren Betrieb nach einem Schadenfall wieder fortsetzen zu können?",
              {
                key: "ea_08",
                label: "Wiederanlaufzeit",
                type: "text",
                placeholder: "z. B. 3 Monate, 6 Wochen …",
              },
              "Geschätzte Wiederanlaufzeit in Wochen oder Monaten",
            ),
          ],
        },
      ],
    },
  ],
};

export const liquiditaetSchema: FormSchema = {
  sections: [
    {
      key: "finanzen",
      title: "Liquidität-Risiken",
      color: "blue",
      subSteps: [
        {
          key: "finanzen",
          title: "Liquidität-Risiken",
          description: "Bitte aufführen – eventuell separate Analyse",
          questions: [
            yn("Vermögenswirksame Leistungen", "fin_01", "fin_01_beschreibung"),
            yn("Sachbezüge nach § 37b EStG", "fin_02", "fin_02_beschreibung"),
            yn(
              "Gesellschafter-Geschäftsführerversorgung",
              "fin_03",
              "fin_03_beschreibung",
            ),
            yn(
              "Stellen Sie Bürgschaften oder sonstige Sicherheiten?",
              "fin_04",
              "fin_04_beschreibung",
            ),
            yn(
              "Sind Zahlungsausfälle Ihrer Kunden denkbar?",
              "fin_05",
              "fin_05_beschreibung",
            ),
            yn(
              "Nutzen Sie Finanzierungen / Leasing / Mietkauf?",
              "fin_06",
              "fin_06_beschreibung",
            ),
            yn(
              "Haben Sie ein professionelles Abrechnungssystem / Rechnungsmanagement / Forderungsmanagement?",
              "fin_07",
              "fin_07_beschreibung",
            ),
            yn("Nutzen Sie Factoring?", "fin_08", "fin_08_beschreibung"),
            yn(
              "Ist es denkbar, dass Mitarbeiter oder sonstige Vertrauenspersonen Geld unterschlagen?",
              "fin_09",
              "fin_09_beschreibung",
            ),
            yn(
              "Sind Kostenrisiken aus rechtlichen Auseinandersetzungen denkbar?",
              "fin_10",
              "fin_10_beschreibung",
            ),
            q("Sonstiges", {
              key: "fin_11",
              label: "Weitere relevante Finanzrisiken",
              type: "textarea",
              placeholder: "Weitere relevante Finanzrisiken …",
            }),
          ],
        },
      ],
    },
  ],
};
